<template>
	<div class="content">
		<section class="page-header">
			<div class="page-header-inner">
				<div class="page-kicker">Open Computational Catalysis Database</div>
				<h1 class="page-title">About Open-ADC</h1>
				<p class="page-desc">
					Open-ADC is an open database for catalytic materials, enabling reproducible computation, structured data access, and accelerated catalyst discovery.
				</p>
			</div>
		</section>

		<section class="about-section">
			<div class="section-head">
				<h2 class="about-section-title">Data Sources &amp; Methodology</h2>
			</div>

			<div class="info-grid">
				<div class="info-panel">
					<h3 class="panel-title">Data Sources</h3>
					<ul class="about-list">
						<li v-for="(line, idx) in dataSourcesLines" :key="idx">
							{{ line }}
						</li>
					</ul>
				</div>

				<div class="info-panel">
					<h3 class="panel-title">Overview</h3>
					<p class="panel-text">
						Open-ADC integrates first-principles calculations, automated
						workflows, and structured data curation to support systematic
						studies of catalytic materials.
					</p>
					<p class="panel-text">
						The platform is designed for reproducible research, descriptor
						construction, and scalable data-driven exploration across
						electrocatalysis, thermocatalysis, and related reaction systems.
					</p>
				</div>
			</div>
		</section>

		<section class="about-section about-who">
			<div class="section-head">
				<h2 class="about-section-title">Who We Are</h2>
			</div>

			<TeamPage2 />

			<div class="about-who-intro">
				<div class="about-who-image">
					<img src="@/assets/whoweare.png" alt="Who We Are" />
				</div>

				<div class="about-who-text">
					<h3>Interdisciplinary Team</h3>

					<p>
						Open-ADC is developed by researchers working across catalysis,
						electronic-structure theory, computational chemistry, and
						scientific data infrastructure.
					</p>

					<p>
						The team builds reproducible workflows for large-scale
						first-principles calculations, descriptor generation, and data
						curation, enabling systematic exploration of catalytic materials
						and reaction environments.
					</p>

					<p>
						Through open data and transparent methodology, we aim to support
						fundamental understanding of catalytic mechanisms and accelerate
						data-driven discovery in catalytic materials research.
					</p>
				</div>
			</div>
		</section>

		<section class="about-section contact-section">
			<div class="section-head">
				<h2 class="about-section-title">Contact Us</h2>
			</div>

			<div class="about-contact-minimal">
				<div
					v-for="(line, idx) in emailLines"
					:key="idx"
					class="about-contact-row"
				>
					<span class="about-contact-value">{{ line }}</span>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import TeamPage2 from '../components/TeamPage2.vue'

export default {
	name: 'CorporateValue',
	components: {
		TeamPage2
	},
	computed: {
		splitLines() {
			return (raw) => {
				if (!raw) return []
				return String(raw)
					.split(/\r?\n/)
					.map((s) => s.trim())
					.filter(Boolean)
			}
		},
		dataSourcesLines() {
			return this.splitLines(this.data && this.data.data_sources)
		},
		emailLines() {
			return this.splitLines(this.data && this.data.about_email)
		}
	},
	data() {
		return {
			data: {}
		}
	},
	methods: {
		getData() {
			this.$api.news.getConfig().then((res) => {
				this.data = res.data.data
				localStorage.setItem('record_number', this.data.record_number.value)

				const userLanguage = 'en'

				this.data.about_email = this.data.about_email.value

				if (userLanguage === 'zh') {
					this.data.about_us = this.data.about_us.value
					this.data.data_sources = this.data.data_sources.value
				} else {
					this.data.about_us = this.data.about_us_en.value
					this.data.data_sources = this.data.data_sources_en.value
				}
			})
		}
	},
	created() {
		this.getData()
	}
}
</script>

<style scoped>
.content {
	min-height: 100vh;
	padding: 24px 0 48px;
	box-sizing: border-box;
	background: #eef2f6;
	font-family: 'Open Sans Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	color: #000000;
}

.page-header,
.about-section {
	max-width: 82%;
	margin: 0 auto;
}

.page-header {
	padding: 18px 0 26px;
	border-bottom: 1px solid #dde5ee;
	margin-bottom: 28px;
}

.page-header-inner {
	max-width: 1600px;
}

.page-kicker {
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: #6b7280;
	margin-bottom: 10px;
}

.page-title {
	margin: 0 0 12px;
	font-size: 34px;
	font-weight: 700;
	line-height: 1.2;
	color: #1f4fa3;
}

.page-desc {
	margin: 0;
	max-width: 1600px;
	font-size: 19px;
	line-height: 1.8;
	color: #475569;
}

.about-section {
	margin-bottom: 28px;
}

.section-head {
	margin-bottom: 14px;
}

.about-section-title {
	margin: 0;
	font-size: 24px;
	font-weight: 700;
	line-height: 1.25;
	color: #1f4fa3;
	text-align: center;
}

.info-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 18px;
}

.info-panel {
	background: #ffffff;
	border: 1px solid #e3e8ef;
	border-radius: 10px;
	padding: 18px 20px;
}

.panel-title {
	margin: 0 0 12px;
	font-size: 18px;
	font-weight: 700;
	color: #1f4fa3;
}

.panel-text {
	margin: 0 0 12px;
	font-size: 16px;
	line-height: 1.85;
	color: #475569;
}

.panel-text:last-child {
	margin-bottom: 0;
}

.about-list {
	margin: 0;
	padding-left: 20px;
	font-size: 16px;
	line-height: 1.9;
	color: #475569;
}

.about-who {
	overflow: visible;
}

.about-who :deep(.reviews_left) {
	display: none;
}

.about-who :deep(.team1) {
	margin-top: 0;
	padding: 0;
}

.about-who :deep(.team_swiper) {
	margin-top: 8px;
}

.about-who-intro {
	margin-top: 22px;
	width: 100%;
	background: #ffffff;
	border: 1px solid #e3e8ef;
	border-radius: 10px;
	padding: 18px 20px;
	display: flex;
	align-items: center;
	gap: 16px;
	box-sizing: border-box;
}

.about-who-image {
	flex: 0 0 24%;
	display: flex;
	justify-content: center;
}

.about-who-image img {
	width: 100%;
	max-width: 220px;
	border-radius: 8px;
	display: block;
	border: 1px solid #e5eaf1;
}

.about-who-text {
	flex: 1;
	color: #475569;
}

.about-who-text h3 {
	margin: 0 0 12px;
	font-size: 20px;
	font-weight: 700;
	color: #1f4fa3;
}

.about-who-text p {
	margin: 0 0 12px;
	font-size: 16px;
	line-height: 1.85;
	color: #475569;
}

.about-who-text p:last-child {
	margin-bottom: 0;
}

.contact-section {
	padding-top: 4px;
}

.about-contact-minimal {
	max-width: 920px;
	margin: 0 auto;
	padding-top: 2px;
	border-top: 1px solid #d9e2ec;
}

.about-contact-row {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 16px 0;
	border-bottom: 1px solid #d9e2ec;
	text-align: center;
}

.about-contact-value {
	display: block;
	font-size: 16px;
	line-height: 1.8;
	color: #475569;
	text-align: center;
}

.about-contact-value {
	display: block;
	word-break: break-all;
	font-size: 16px;
	line-height: 1.8;
	color: #475569;
}

@media (max-width: 1100px) {
	.info-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 960px) {
	.page-header,
	.about-section {
		max-width: calc(100% - 28px);
	}

	.page-title {
		font-size: 28px;
	}

	.page-desc,
	.about-list,
	.panel-text,
	.about-who-text p,
	.about-contact-value {
		font-size: 15px;
	}

	.about-who-intro {
		flex-direction: column;
		align-items: stretch;
	}

	.about-who-image {
		flex: none;
	}

	.about-who-image img {
		max-width: 220px;
		margin: 0 auto;
	}

	.about-contact-row {
	flex-direction: column;
	align-items: center;
	gap: 6px;
    }
}
</style>